import type { UserRole } from '@/types';

/** Display-only cookie. Not a credential — the header reads it to show signed-in chrome. */
export const VIEWER_COOKIE = 'kleaners-viewer'; // pragma: allowlist secret

/** Matches the refresh-token lifetime so marketing pages keep the signed-in chrome. */
export const VIEWER_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

const NAME_MAX_LENGTH = 80;

export interface ViewerSnapshot {
  name: string;
  role: UserRole;
}

const ROLES: readonly UserRole[] = ['user', 'provider', 'admin'];

export function sanitizeViewerName(name: string): string {
  return name
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim()
    .slice(0, NAME_MAX_LENGTH);
}

function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && ROLES.includes(value as UserRole);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export function encodeViewerCookie(viewer: ViewerSnapshot): string {
  const payload = JSON.stringify({
    name: sanitizeViewerName(viewer.name),
    role: viewer.role,
  });
  return bytesToBase64Url(new TextEncoder().encode(payload));
}

export function decodeViewerCookie(value: string | null | undefined): ViewerSnapshot | null {
  if (!value) {
    return null;
  }

  try {
    const json = new TextDecoder().decode(base64UrlToBytes(value));
    const parsed = JSON.parse(json) as { name?: unknown; role?: unknown };
    if (typeof parsed.name !== 'string' || !isUserRole(parsed.role)) {
      return null;
    }
    return { name: sanitizeViewerName(parsed.name), role: parsed.role };
  } catch {
    return null;
  }
}

export function viewerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) {
    return '';
  }
  const firstChar = Array.from(first)[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  const lastChar = last ? (Array.from(last)[0] ?? '') : '';
  return `${firstChar}${lastChar}`.toLocaleUpperCase();
}

let cachedEncoded: string | null | undefined;
let cachedSnapshot: ViewerSnapshot | null = null;

function readEncodedViewerCookie(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${VIEWER_COOKIE}=`;
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return part.slice(prefix.length);
    }
  }
  return null;
}

/** Stable snapshot for `useSyncExternalStore`. */
export function readViewerCookieSnapshot(): ViewerSnapshot | null {
  const encoded = readEncodedViewerCookie();
  if (encoded === cachedEncoded) {
    return cachedSnapshot;
  }
  cachedEncoded = encoded;
  cachedSnapshot = encoded ? decodeViewerCookie(encoded) : null;
  return cachedSnapshot;
}

export function getServerViewerSnapshot(): null {
  return null;
}

export function subscribeViewerCookie(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const notify = () => {
    onStoreChange();
  };
  window.addEventListener('focus', notify);
  window.addEventListener('visibilitychange', notify);
  return () => {
    window.removeEventListener('focus', notify);
    window.removeEventListener('visibilitychange', notify);
  };
}
